import { useId, useState } from "react";
import type {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import {
    LoadingAndEmptyScenario,
    type DataState,
    createLoadingAndEmptyState,
} from "./viewTablePlayground/LoadingAndEmptyScenario";
import {
    NestedDragAndDropScenario,
    type AcceptedNestedDrop,
    createNestedDragAndDropState,
} from "./viewTablePlayground/NestedDragAndDropScenario";
import {
    PinnedAndSortableScenario,
    type PinnedSort,
    createPinnedAndSortableState,
} from "./viewTablePlayground/PinnedAndSortableScenario";
import {
    RowReorderingScenario,
    createRowReorderingState,
} from "./viewTablePlayground/RowReorderingScenario";
import {
    DENSITY_OPTIONS,
    type NestedShowcaseRow,
    type ReorderRow,
    mergePlaygroundView,
} from "./viewTablePlayground/viewTablePlaygroundShared";
import styles from "./ViewTablePlayground.module.scss";

type ScenarioId = "pinned" | "reorder" | "nested" | "data-state";

const SCENARIOS = [
    { value: "pinned", label: "Pinned and sortable" },
    { value: "reorder", label: "Row reordering" },
    { value: "nested", label: "Nested drag and drop" },
    { value: "data-state", label: "Loading and empty states" },
] satisfies ReadonlyArray<{ value: ScenarioId; label: string }>;

/**
 * @purpose Showcases the promoted `ViewTable` engine one capability at a time:
 * pinned/sortable/resizable columns with selection and totals, flat row drag
 * and drop, nested drag and drop with rejected drops, and the engine's own
 * loading and empty states. Every scenario's durable state lives here so
 * switching scenarios — or changing density — never discards the work done in
 * another one, while `Reset` returns all of them to their starting fixtures.
 */
export const ViewTablePlayground = () => {
    const scenarioLabelId = useId();
    const [activeScenario, setActiveScenario] = useState<ScenarioId>("pinned");
    const [density, setDensity] = useState(ViewDensity.MD);
    const [pinnedState, setPinnedState] = useState(
        createPinnedAndSortableState,
    );
    const [reorderingState, setReorderingState] = useState(
        createRowReorderingState,
    );
    const [nestedState, setNestedState] = useState(
        createNestedDragAndDropState,
    );
    const [loadingState, setLoadingState] = useState(
        createLoadingAndEmptyState,
    );

    const handlePinnedViewChange = (nextView: View) => {
        setPinnedState((current) => ({
            ...current,
            view: mergePlaygroundView({
                currentView: current.view,
                nextView,
            }),
        }));
    };

    const handlePinnedSortChange = (sort: PinnedSort) => {
        setPinnedState((current) => ({ ...current, sort }));
    };

    const handlePinnedSortRemove = () => {
        setPinnedState((current) => ({ ...current, sort: null }));
    };

    const handlePinnedSelectionChange: OnChangeFn<RowSelectionState> = (
        updater,
    ) => {
        setPinnedState((current) => ({
            ...current,
            rowSelection:
                typeof updater === "function"
                    ? updater(current.rowSelection)
                    : updater,
        }));
    };

    const handleReorderRowsChange = (rows: ReorderRow[]) => {
        setReorderingState((current) => ({ ...current, rows }));
    };

    const handleNestedExpandedChange: OnChangeFn<ExpandedState> = (updater) => {
        setNestedState((current) => ({
            ...current,
            expanded:
                typeof updater === "function"
                    ? updater(current.expanded)
                    : updater,
        }));
    };

    const handleNestedAcceptedDrop = ({
        rows,
        expanded,
        lastDrop,
    }: {
        rows: NestedShowcaseRow[];
        expanded: ExpandedState;
        lastDrop: AcceptedNestedDrop;
    }) => {
        setNestedState((current) => ({ ...current, rows, expanded, lastDrop }));
    };

    const handleDataStateChange = (dataState: DataState) => {
        setLoadingState((current) => ({ ...current, dataState }));
    };

    const handleReset = () => {
        setDensity(ViewDensity.MD);
        setPinnedState(createPinnedAndSortableState());
        setReorderingState(createRowReorderingState());
        setNestedState(createNestedDragAndDropState());
        setLoadingState(createLoadingAndEmptyState());
    };

    const renderScenario = () => {
        switch (activeScenario) {
            case "pinned":
                return (
                    <PinnedAndSortableScenario
                        density={density}
                        state={pinnedState}
                        onViewChange={handlePinnedViewChange}
                        onSortChange={handlePinnedSortChange}
                        onSortRemove={handlePinnedSortRemove}
                        onRowSelectionChange={handlePinnedSelectionChange}
                    />
                );
            case "reorder":
                return (
                    <RowReorderingScenario
                        density={density}
                        state={reorderingState}
                        onRowsChange={handleReorderRowsChange}
                    />
                );
            case "nested":
                return (
                    <NestedDragAndDropScenario
                        density={density}
                        state={nestedState}
                        onExpandedChange={handleNestedExpandedChange}
                        onAcceptedDrop={handleNestedAcceptedDrop}
                    />
                );
            case "data-state":
                return (
                    <LoadingAndEmptyScenario
                        density={density}
                        state={loadingState}
                        onDataStateChange={handleDataStateChange}
                    />
                );
        }
    };

    const activeScenarioLabel = SCENARIOS.find(
        (scenario) => scenario.value === activeScenario,
    )?.label;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2 id={scenarioLabelId}>Scenario</h2>
                        <PlaygroundSelect<ScenarioId>
                            options={SCENARIOS}
                            value={activeScenario}
                            onChange={setActiveScenario}
                            aria-labelledby={scenarioLabelId}
                        />
                    </section>
                    <section>
                        <h2>Density</h2>
                        <ToggleGroup.Root<ViewDensity>
                            value={density}
                            onValueChange={(nextDensity) => {
                                if (nextDensity) {
                                    setDensity(nextDensity);
                                }
                            }}
                        >
                            {DENSITY_OPTIONS.map((option) => (
                                <ToggleGroup.Item
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Showcase state</h2>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={handleReset}
                        >
                            Reset
                        </Button>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={activeScenarioLabel}
                width="full"
                alignment="stretch"
            >
                <div className={styles.scenarioStage}>{renderScenario()}</div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
